// StartCaptureCard — "Ready to start" / "Ready to resume" empty-state
// rendered by BuildingCaptureScreen (scope='building') and
// SiteCaptureScreen (scope='site') when the deep-link load returns
// session === null for a field-staff user (buildspec 20-ui §1.1, §5.4b).
// Holds the single explicit "Start capture" gesture that promotes a
// safe-read landing into an actual capture session.
//
// Resume framing: when `buildingStatus` is 'complete' (already captured)
// or 'in_progress' (orphan — captured by someone else, no active session
// owned by this user) the copy flips to a "resume" framing — heading,
// body, and button label all adapt. The underlying action remains the
// same POST /capture/sessions — only the copy changes.
//
// Brand: signature CTA pairing — Forest panel, Lime headline, Cream
// body copy, Lime CTA pill (skill: "CTA block" → Forest BG, Lime
// heading, Cream body, Lime-on-dark button).
//
// `scope` defaults to 'building' so existing callers (BuildingCaptureScreen,
// W1.5) keep working unchanged. Passing scope='site' adapts the button
// label and body copy for site-level capture.
//
// `isStarting` renders the button in a "Starting…" / "Resuming…" /
// disabled state while the POST is in-flight. Optional — when undefined
// the button is NOT placed in a starting state (i.e. undefined === false).
//
// `onStart` is optional while the gesture → POST path is still under
// construction. When undefined the button renders disabled so an
// unwired render cannot silently drop a field-staff tap.
//
// Tap target: the primary Button below already enforces the 44×44 px
// minimum via `<Button>` base classes (AGENTS.md invariant #10). Copy
// is white-label per AGENTS.md §1 — no client-specific branding.

import type { ReactElement } from 'react'

import { isAdminOnlyBuildingStatus } from '@citycare/shared'
import type { BuildingStatus } from '@citycare/shared'

import { Button } from '../../ui/button'

import { BuildingStatusBadge } from './building-status-badge'

export interface StartCaptureCardProps {
  buildingName: string
  buildingFloc: string | null
  /**
   * Whether this card is for building-level or site-level capture.
   * Defaults to 'building' so all existing callers remain unchanged.
   */
  scope?: 'building' | 'site'
  /**
   * True while the POST /capture/sessions request is in-flight.
   * Renders the button as "Starting…" and disabled to prevent double-tap.
   * Optional — defaults to false.
   */
  isStarting?: boolean
  /**
   * Status of the building being captured. When 'complete' (or
   * 'in_progress' with no active session owned by this user — the
   * orphan case), the card flips its copy to a "resume" framing —
   * heading, body, and button label all adapt. An admin-only status
   * (to_be_demolished / out_of_scope) renders a read-only state: no
   * Start/Resume button, just the status pill (audit-1-#7 — the server
   * rejects session create/resume on frozen buildings, so the client
   * must not offer the gesture). Defaults to undefined
   * → treated as not_started (current "Ready to start" copy). The
   * site-scope caller does not pass this prop because a site does
   * not have a single status.
   */
  buildingStatus?: BuildingStatus
  // Handler for the "Start capture" gesture. Optional while the
  // gesture → POST path is still under construction. When undefined
  // the button renders disabled so an unwired render cannot silently
  // drop a field-staff tap.
  onStart?: () => void
}

export function StartCaptureCard({
  buildingName,
  buildingFloc,
  scope = 'building',
  isStarting = false,
  buildingStatus,
  onStart,
}: StartCaptureCardProps): ReactElement {
  const isSite = scope === 'site'
  // audit-1-#7: a frozen building (admin-only status) is not capture work —
  // the card must not offer Start/Resume. Mirrors the badge's approach:
  // read-only status pill, no actionable control.
  const isFrozen =
    buildingStatus !== undefined && isAdminOnlyBuildingStatus(buildingStatus)
  const isResume =
    buildingStatus === 'complete' || buildingStatus === 'in_progress'
  const disabled = onStart === undefined || isStarting

  const inFlightLabel = isResume ? 'Resuming…' : 'Starting…'
  const idleLabel = isResume
    ? isSite
      ? 'Resume site capture'
      : 'Resume capture'
    : isSite
      ? 'Start site capture'
      : 'Start capture'
  const buttonLabel = isStarting ? inFlightLabel : idleLabel

  const heading = isFrozen
    ? 'Not available for capture'
    : isResume
      ? 'Ready to resume'
      : 'Ready to start'

  const bodyCopy = isSite
    ? 'recording site-level assets'
    : 'counting assets'

  return (
    <section
      aria-labelledby="start-capture-heading"
      className="overflow-hidden rounded-2xl bg-[color:var(--color-cc-forest)] p-6 text-[color:var(--color-cc-cream)] shadow-sm sm:p-8"
    >
      <span className="inline-flex items-center gap-2 rounded-full bg-[color:var(--color-cc-cream)]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-lime)]">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
        {isSite ? 'Site capture' : 'Building capture'}
      </span>
      <h2
        id="start-capture-heading"
        className="mt-3 font-display text-3xl font-semibold leading-tight text-[color:var(--color-cc-lime)] sm:text-4xl"
      >
        {heading}
      </h2>
      <p className="mt-3 text-sm text-[color:var(--color-cc-cream)]/70 sm:text-base">
        {isResume || isFrozen ? null : <>No active capture session for </>}
        <span className="font-semibold text-[color:var(--color-cc-cream)]">
          {buildingName}
        </span>
        {buildingFloc !== null ? (
          <>
            {' '}
            <span className="text-[color:var(--color-cc-cream)]/50">
              ({buildingFloc})
            </span>
          </>
        ) : null}
        {isFrozen
          ? ' has been marked by an admin and is not available for capture.'
          : isResume
            ? ' was already captured. Tap below to resume and edit.'
            : `. Tap below to begin ${bodyCopy}.`}
      </p>
      <div className="mt-6">
        {isFrozen && buildingStatus !== undefined ? (
          // Read-only status pill (no onToggle) — the same treatment the
          // badge gives admin-only statuses everywhere else.
          <BuildingStatusBadge status={buildingStatus} />
        ) : (
          <Button variant="lime" disabled={disabled} onClick={onStart}>
            {buttonLabel}
          </Button>
        )}
      </div>
    </section>
  )
}
